import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type NaZakazkuPolozkaPreview = {
  code: string;
  variant_name: string | null;
  kind: 'na_zakazku' | 'termin' | 'skladem';
  level: number | null;
  datum: string | null;
};

type NaZakazkuOrderPreview = {
  eshop: string;
  country: 'CZ' | 'SK';
  lhuta_dni: number;
  status_name: string;
  order_code: string;
  order_date: string | null;
  customer_email: string | null;
  items: NaZakazkuPolozkaPreview[];
  expected_term: string | null;
  navrhovany_termin: string | null;
  akce: 'vyrizuje_se' | 'zaradit';
  cilovy_stav: string | null;
  poznamka: string | null;
  rozhodujici_polozka: string | null;
  problem: string | null;
  potvrzeno: boolean;
};

type PreviewResult = {
  orders: NaZakazkuOrderPreview[];
  errors: string[];
};

type ZaraditResult = {
  ok: boolean;
  items: { sku: string; ok: boolean; error?: string; already_done?: boolean }[];
  status_switched: boolean;
  target_status?: string;
  error?: string;
};

/** ISO datum ze Shoptetu ('2026-04-20T22:30:00+0000') → '20.4.2026 22:30'. */
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 'YYYY-MM-DD' → '31.7.2026'. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${Number(d)}.${Number(m)}.${y}` : iso;
}

const KIND_LABEL: Record<NaZakazkuPolozkaPreview['kind'], string> = {
  na_zakazku: 'na zakázku',
  termin: 'termín',
  skladem: 'skladem',
};

/** Text jedné položky pro sloupec Položky — např. "L1068/122 (na zakázku do 14 dní → 9.10.2026)". */
function polozkaText(i: NaZakazkuPolozkaPreview): string {
  const label = KIND_LABEL[i.kind];
  const detail =
    i.kind === 'na_zakazku'
      ? `${label} do ${i.level} dní${i.datum ? ` → ${fmtDate(i.datum)}` : ''}`
      : i.kind === 'termin'
        ? `${label} ${i.datum ? fmtDate(i.datum) : ''}`
        : label;
  return `${i.code} (${detail})`;
}

/**
 * Výpis položek objednávky — ta, podle které se vybral navrhovaný termín
 * (`rozhodujici`), je tučně a zeleně zvýrazněná, ať jde na první pohled
 * ověřit, že logika vzala opravdu tu nejpozdější.
 */
function PolozkyList({
  items,
  rozhodujici,
}: {
  items: NaZakazkuPolozkaPreview[];
  rozhodujici: string | null;
}) {
  return (
    <>
      {items.map((i, idx) => (
        <span key={i.code}>
          {idx > 0 && ', '}
          <span className={i.code === rozhodujici ? 'font-semibold text-success' : undefined}>
            {polozkaText(i)}
          </span>
        </span>
      ))}
    </>
  );
}

/**
 * Náhled na třídění NOVÝCH objednávek (od nasazení dál) ve stavech „Ušijeme
 * (pro Vás/pre vás) do 7/10/14 dní" — pro každou položku objednávky zjistí,
 * jestli je na zakázku, nebo už rozplánovaná na konkrétní termín ve výrobě,
 * a objednávka se řídí tou NEJPOZDĚJŠÍ z nich. Když nic nezbývá, poslední
 * pojistkou je živá skladovost → „Vyřizuje se". Jen výpis — nic se tu
 * nepřepíná, nic se nezapisuje, žádný e-mail se neposílá.
 */
export function NaZakazkuTrideniList() {
  const [data, setData] = useState<PreviewResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [potvrzujeSe, setPotvrzujeSe] = useState<Set<string>>(new Set());
  const [zarazujeSe, setZarazujeSe] = useState<Set<string>>(new Set());
  const [zarazitChyba, setZarazitChyba] = useState<Map<string, string>>(new Map());

  const load = useCallback(async () => {
    setData(null);
    setErr(null);
    try {
      const d = await apiFetch<PreviewResult>('/api/vyroba/na-zakazku/trideni-nahled');
      setData(d);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Načtení selhalo');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Klik na řádek — potvrdí/zruší potvrzení, uloží se rovnou (bez reloadu). */
  const togglePotvrzeno = useCallback(async (order: NaZakazkuOrderPreview) => {
    const key = `${order.eshop}\u0000${order.order_code}`;
    setPotvrzujeSe((s) => new Set(s).add(key));
    // Optimisticky přepnout hned, ať to nečeká na odpověď serveru.
    setData((prev) =>
      prev
        ? {
            ...prev,
            orders: prev.orders.map((o) =>
              o.eshop === order.eshop && o.order_code === order.order_code
                ? { ...o, potvrzeno: !o.potvrzeno }
                : o,
            ),
          }
        : prev,
    );
    try {
      await apiFetch('/api/vyroba/na-zakazku/trideni-nahled/potvrdit', {
        method: 'POST',
        body: { eshop: order.eshop, order_code: order.order_code },
      });
    } catch {
      // Neúspěch → vrátit zpátky, jak to bylo.
      setData((prev) =>
        prev
          ? {
              ...prev,
              orders: prev.orders.map((o) =>
                o.eshop === order.eshop && o.order_code === order.order_code
                  ? { ...o, potvrzeno: order.potvrzeno }
                  : o,
              ),
            }
          : prev,
      );
    } finally {
      setPotvrzujeSe((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }, []);

  /**
   * Skutečné zařazení objednávky do výroby — zapíše na-zakázku položky do
   * evidence + Shoptet skladu (stejná cesta jako ruční „Zadání do výroby") a
   * přepne stav objednávky BEZ e-mailu. Po úspěchu objednávka zmizí ze
   * seznamu, protože už není ve stavu „Ušijeme do X dní" (přirozeně vypadne
   * při dalším Načíst znovu) — pro okamžitou odezvu ji smažeme rovnou.
   */
  const zaraditDoVyroby = useCallback(async (order: NaZakazkuOrderPreview) => {
    const key = `${order.eshop}\u0000${order.order_code}`;
    setZarazujeSe((s) => new Set(s).add(key));
    setZarazitChyba((m) => {
      const next = new Map(m);
      next.delete(key);
      return next;
    });
    try {
      const res = await apiFetch<ZaraditResult>('/api/vyroba/na-zakazku/trideni-nahled/zaradit', {
        method: 'POST',
        body: { eshop: order.eshop, order_code: order.order_code },
      });
      if (res.ok) {
        setData((prev) =>
          prev
            ? {
                ...prev,
                orders: prev.orders.filter(
                  (o) => !(o.eshop === order.eshop && o.order_code === order.order_code),
                ),
              }
            : prev,
        );
      } else {
        const chyba =
          res.error ??
          res.items.find((i) => !i.ok)?.error ??
          'Zařazení do výroby se nepovedlo u některé položky';
        setZarazitChyba((m) => new Map(m).set(key, chyba));
      }
    } catch (e: unknown) {
      setZarazitChyba((m) =>
        new Map(m).set(key, e instanceof ApiError ? e.describe() : 'Zařazení se nepovedlo'),
      );
    } finally {
      setZarazujeSe((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const orders = data?.orders ?? [];
  const counts = new Map<number, number>();
  for (const o of orders) counts.set(o.lhuta_dni, (counts.get(o.lhuta_dni) ?? 0) + 1);
  const problemCount = orders.filter((o) => o.problem).length;
  const vyrizujeSeCount = orders.filter((o) => o.akce === 'vyrizuje_se').length;

  return (
    <>
      <div className="bg-warning-light border border-warning/40 text-text rounded-lg px-4 py-3 mb-4 text-sm font-semibold">
        Slouží už jen pro vizuální kontrolu — samotné zařazení do výroby už kompletně dělá CRON
        (každých 5 minut, bez čekání na kliknutí).
      </div>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <p className="text-sm text-text-secondary max-w-3xl">
          NOVÉ objednávky (CZ i SK, od nasazení téhle záložky dál) ve stavu „Ušijeme do 7/10/14 dní"
          — u každé položky se zjistí, jestli je na zakázku, nebo už rozplánovaná na konkrétní
          termín, a rozhoduje ta <strong>nejpozdější</strong>. Tlačítko „Zařadit do výroby" zůstává
          pro ruční zásah, ale běžně na něj není potřeba sahat — cron zařadí všechno bez problému
          sám.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors shrink-0"
        >
          Načíst znovu
        </button>
      </div>

      {err && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {err}
        </div>
      )}

      {data && data.errors.length > 0 && (
        <ul className="mb-4 space-y-0.5 text-[13px] text-danger">
          {data.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
          {[7, 10, 14].map((dni) => (
            <div key={dni} className="bg-surface rounded-xl shadow-card px-5 py-4">
              <div className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                Do {dni} dní
              </div>
              <div className="text-xl font-bold mt-1 text-text">{counts.get(dni) ?? 0}</div>
              <div className="text-xs text-text-muted mt-0.5">objednávek</div>
            </div>
          ))}
          <div className="bg-surface rounded-xl shadow-card px-5 py-4">
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Vyřizuje se
            </div>
            <div className="text-xl font-bold mt-1 text-text">{vyrizujeSeCount}</div>
            <div className="text-xs text-text-muted mt-0.5">nic už nechybí</div>
          </div>
          <div className="bg-surface rounded-xl shadow-card px-5 py-4">
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wide">
              Problém
            </div>
            <div
              className={`text-xl font-bold mt-1 ${problemCount > 0 ? 'text-danger' : 'text-text'}`}
            >
              {problemCount}
            </div>
            <div className="text-xs text-text-muted mt-0.5">nesedí termín/sklad</div>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
              <th className="px-4 py-2.5">E-shop</th>
              <th className="px-4 py-2.5">Objednávka</th>
              <th className="px-4 py-2.5">Datum objednávky</th>
              <th className="px-4 py-2.5">Aktuální stav</th>
              <th className="px-4 py-2.5">Položky</th>
              <th className="px-4 py-2.5">Rozhoduje</th>
              <th className="px-4 py-2.5">Navrhovaná akce</th>
              <th className="px-4 py-2.5">Zařadit</th>
              <th className="px-4 py-2.5 text-center">Zkontrolováno</th>
            </tr>
          </thead>
          <tbody>
            {data == null ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-text-muted">
                  Načítám… (stahuje se živě ze Shoptetu, chvíli to trvá)
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-text-muted">
                  Žádná nová objednávka teď ve stavu „Ušijeme do X dní" není.
                </td>
              </tr>
            ) : (
              orders.map((o) => {
                const key = `${o.eshop}\u0000${o.order_code}`;
                const ukladaSe = potvrzujeSe.has(key);
                const zarazujeSeTed = zarazujeSe.has(key);
                const chybaZarazeni = zarazitChyba.get(key);
                return (
                  <tr
                    key={`${o.eshop}-${o.order_code}`}
                    className={`border-b border-border-light align-top transition-colors ${
                      o.potvrzeno ? 'bg-success-light' : ''
                    } ${ukladaSe ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-1.5 text-text-secondary whitespace-nowrap">
                      {o.country}
                    </td>
                    <td className="px-4 py-1.5 font-semibold text-text whitespace-nowrap">
                      {o.order_code}
                      {o.customer_email && (
                        <div className="text-xs font-normal text-text-muted">
                          {o.customer_email}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-text-secondary whitespace-nowrap">
                      {fmtDateTime(o.order_date)}
                    </td>
                    <td className="px-4 py-1.5 text-text-secondary whitespace-nowrap">
                      {o.status_name}
                    </td>
                    <td className="px-4 py-1.5 text-text-secondary max-w-xs">
                      <PolozkyList items={o.items} rozhodujici={o.rozhodujici_polozka} />
                    </td>
                    <td className="px-4 py-1.5 text-text-secondary max-w-[220px]">
                      {o.poznamka ?? (
                        <span className="text-text-muted">nic ve výrobě ani na zakázku</span>
                      )}
                    </td>
                    <td className="px-4 py-1.5 font-semibold whitespace-nowrap">
                      {o.problem ? (
                        <span className="text-danger font-normal">{o.problem}</span>
                      ) : o.akce === 'vyrizuje_se' ? (
                        <span className="text-success">✓ Vyřizuje se</span>
                      ) : (
                        <span className="text-text">
                          → {o.cilovy_stav ?? fmtDate(o.navrhovany_termin)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => zaraditDoVyroby(o)}
                        disabled={!!o.problem || zarazujeSeTed}
                        title={
                          o.problem
                            ? 'Nejde zařadit, dokud není vyřešený problém'
                            : 'Zapíše na-zakázku položky do výroby (+1 sklad v Shoptetu) a přepne stav objednávky bez e-mailu'
                        }
                        className="text-xs font-semibold text-white bg-success rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-opacity"
                      >
                        {zarazujeSeTed ? 'Zařazuji…' : 'Zařadit do výroby'}
                      </button>
                      {chybaZarazeni && (
                        <div className="text-xs text-danger mt-1 max-w-[200px]">
                          {chybaZarazeni}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => togglePotvrzeno(o)}
                        disabled={ukladaSe}
                        title="Kliknutím označíš, že jsi řádek zkontrolovala/zkontroloval"
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs cursor-pointer transition-colors ${
                          o.potvrzeno
                            ? 'bg-success border-success text-white hover:opacity-80'
                            : 'border-border text-transparent hover:border-text-muted hover:bg-surface-hover'
                        }`}
                      >
                        ✓
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
